import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Image, 
  Trash, 
  CheckCircle, 
  Warning,
  Copy,
  ArrowRight,
  ArrowsClockwise,
  SpinnerGap,
  Eye,
  DownloadSimple,
  ClockCounterClockwise,
  FilePdf,
  FileText,
  FileVideo,
  File as FileIcon
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const proxyImg = (url) => url ? `${API}/api/media/proxy?url=${encodeURIComponent(url)}` : '';

const FileTypeIcon = ({ mimeType, className = '' }) => {
  const mt = (mimeType || '').toLowerCase();
  const cls = `w-12 h-12 ${className}`;
  if (mt === 'application/pdf') return <FilePdf size={48} weight="duotone" className={`text-red-500 ${className}`} />;
  if (mt.startsWith('font/') || mt.includes('woff')) return <FileText size={48} weight="duotone" className={`text-purple-500 ${className}`} />;
  if (mt.startsWith('video/')) return <FileVideo size={48} weight="duotone" className={`text-blue-500 ${className}`} />;
  if (mt === 'text/html') return <FileText size={48} weight="duotone" className={`text-orange-500 ${className}`} />;
  return <FileIcon size={48} weight="duotone" className={`text-muted-foreground ${className}`} />;
};

const MediaThumb = ({ item, className = '', alt = '' }) => {
  const isImage = (item.mime_type || '').toLowerCase().startsWith('image/');
  if (isImage) {
    return (
      <img
        src={proxyImg(item.thumbnail || item.source_url)}
        alt={alt}
        className={className}
        referrerPolicy="no-referrer"
        loading="lazy"
        onError={(e) => { e.target.style.opacity = '0.3'; }}
      />
    );
  }
  return (
    <div className={`${className} flex flex-col items-center justify-center bg-muted/40 gap-1`}>
      <FileTypeIcon mimeType={item.mime_type} />
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
        {(item.mime_type || '?').split('/')[1] || item.mime_type || 'fil'}
      </span>
    </div>
  );
};

export default function MediaPage() {
  const [scanStatus, setScanStatus] = useState(null);
  const [altStatus, setAltStatus] = useState(null);
  const [webpStatus, setWebpStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [unused, setUnused] = useState({ items: [], total: 0, page: 1 });
  const [duplicates, setDuplicates] = useState({ groups: [], total: 0, page: 1 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [perPage, setPerPage] = useState(50);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    fetchAll();
    // Poll scan status + alt-text status every 5s so the user sees progress
    // tick in real time without manually refreshing.
    const interval = setInterval(() => {
      fetchScanStatus();
      fetchAltStatus();
      fetchWebpStatus();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = async () => {
    await Promise.all([
      fetchScanStatus(), fetchAltStatus(), fetchWebpStatus(), fetchOverview(),
      fetchUnused(), fetchDuplicates(), fetchHistory(),
    ]);
    setLoading(false);
  };

  const fetchAltStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/alt-text/status`, { headers: getHeaders() });
      setAltStatus(data);
      if (data?.running) fetchOverview();
    } catch {}
  };

  const fetchWebpStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/webp/status`, { headers: getHeaders() });
      setWebpStatus(data);
      if (data?.running) fetchOverview();
    } catch {}
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/scan/history?limit=10`, { headers: getHeaders() });
      setHistory(data.items || []);
    } catch {
      /* ignore */
    }
  };

  const exportUnused = async () => {
    try {
      const res = await axios.get(`${API}/api/media/unused/export`, {
        headers: getHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ubrukte-bilder-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel-fil lastet ned');
    } catch (err) {
      toast.error('Kunne ikke laste ned');
    }
  };

  const fetchScanStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/scan/status`, { headers: getHeaders() });
      setScanStatus((prev) => {
        // Detect transition from running → completed and surface a summary toast
        if (
          prev?.running &&
          !data.running &&
          data.phase === 'completed' &&
          data.completed_at !== prev.completed_at
        ) {
          const newItems = data.new_items || 0;
          const fallback = data.fallback_recovered || 0;
          const recoveredFromUnused = data.recovered_from_unused || 0;
          const unused = data.unused_found ?? 0;
          const dups = data.duplicates_found ?? 0;
          const parts = [];
          if (newItems > 0) parts.push(`${newItems} nye bilder hentet`);
          if (recoveredFromUnused > 0) parts.push(`${recoveredFromUnused} bilder reddet fra "ubrukt"`);
          if (fallback > 0) parts.push(`${fallback} bilder reddet via wp_parent-fallback`);
          parts.push(`${unused} ubrukt`);
          if (dups > 0) parts.push(`${dups} duplikat-grupper`);
          toast.success(`Skann fullført — ${parts.join(' · ')}`, { duration: 8000 });
          fetchOverview();
          fetchUnused(1);
          fetchDuplicates(1);
          fetchHistory();
        }
        if (data.phase === 'completed' && overview?.total === 0) {
          fetchOverview();
          fetchUnused();
          fetchDuplicates();
        }
        return data;
      });
    } catch {
      /* ignore */
    }
  };

  const fetchOverview = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/overview`, { headers: getHeaders() });
      setOverview(data);
    } catch {}
  };

  const fetchUnused = async (page = 1) => {
    try {
      const { data } = await axios.get(`${API}/api/media/unused?page=${page}&limit=${perPage}`, { headers: getHeaders() });
      setUnused(data);
      setSelectedMedia([]);
    } catch {}
  };

  const fetchDuplicates = async (page = 1) => {
    try {
      const { data } = await axios.get(`${API}/api/media/duplicates?page=${page}&limit=10`, { headers: getHeaders() });
      setDuplicates(data);
    } catch {}
  };

  const refreshScan = async (mode = 'verify') => {
    // mode: 'verify' (re-check ubrukte only - idempotent), 'remap' (full re-mapping), 'incremental' (last 30 days), 'full' (re-fetch + remap)
    const messages = {
      verify: 'Sjekker ubrukte bilder — bevarer eksisterende koblinger, kun de feilmerkede oppdages. ~30 sekunder.',
      remap: 'Full re-mapping — nullstiller alle koblinger og bygger fra nytt. ~2-3 minutter. Bruk kun hvis data ser korrupte ut.',
      incremental: 'Inkrementell skann — henter kun nye bilder fra siste 30 dager. ~1-2 minutter.',
      full: 'Full skanning henter ALLE bilder fra WordPress (kan være tusenvis). Tar 5-15 minutter. Fortsett?',
    };
    if (mode === 'remap' && !window.confirm(`${messages.remap}\n\nFortsett?`)) return;
    if (mode === 'full' && !window.confirm(messages.full)) return;

    try {
      let endpoint = '/api/media/scan/verify-unused';
      if (mode === 'remap') endpoint = '/api/media/scan/remap';
      if (mode === 'incremental') endpoint = '/api/media/scan/start?days=30';
      if (mode === 'full') endpoint = '/api/media/scan/start';
      await axios.post(`${API}${endpoint}`, {}, { headers: getHeaders() });
      toast.success(messages[mode]);
      setTimeout(() => { fetchOverview(); fetchUnused(1); fetchScanStatus(); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke starte');
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette bildet permanent?')) return;
    try {
      await axios.post(`${API}/api/media/delete/${mediaId}`, {}, { headers: getHeaders() });
      toast.success('Bilde slettet');
      fetchUnused(unused.page);
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke slette');
    }
  };

  const protectMedia = async (mediaId, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.post(`${API}/api/media/protect/${mediaId}`, {}, { headers: getHeaders() });
      toast.success('Bildet er beskyttet — vil ikke vises som ubrukt');
      fetchUnused(unused.page);
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke beskytte');
    }
  };

  const replaceDuplicate = async (keepId, deleteIds) => {
    if (!window.confirm(`Behold bilde ${keepId} og slett ${deleteIds.length} duplikater? Alle produkter oppdateres automatisk.`)) return;
    try {
      const { data } = await axios.post(`${API}/api/media/replace-duplicate`, { keep_id: keepId, delete_ids: deleteIds }, { headers: getHeaders() });
      toast.success(`${data.deleted_media} duplikater slettet, ${data.updated_products} produkter oppdatert`);
      fetchDuplicates(duplicates.page);
      fetchOverview();
    } catch (err) {
      toast.error('Feil ved erstatning');
    }
  };

  const runBatchDelete = async () => {
    if (selectedMedia.length === 0) {
      toast.error('Marker bilder du vil slette først');
      return;
    }
    if (!window.confirm(`Slett ${selectedMedia.length} markerte bilder permanent fra WordPress? Dette kan ikke angres.`)) return;
    setDeleting(true);
    try {
      let deleted = 0;
      let protected_count = 0;
      for (const id of selectedMedia) {
        try {
          await axios.post(`${API}/api/media/delete/${id}`, {}, { headers: getHeaders() });
          deleted++;
        } catch (err) {
          if (err.response?.status === 403) protected_count++;
        }
      }
      const msg = protected_count > 0
        ? `${deleted} bilder slettet · ${protected_count} beskyttet (PDF/font) ble hoppet over`
        : `${deleted} bilder slettet`;
      toast.success(msg, { duration: 6000 });
      setSelectedMedia([]);
      fetchUnused(unused.page);
      fetchOverview();
    } catch (err) {
      toast.error('Feil ved sletting');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectMedia = (id) => {
    setSelectedMedia(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const selectAllOnPage = () => {
    if (selectedMedia.length === unused.items.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(unused.items.map(i => i.id));
    }
  };

  const changePerPage = (value) => {
    setPerPage(parseInt(value));
    setSelectedMedia([]);
    setTimeout(() => fetchUnused(1), 100);
  };

  const runWebpConvert = async () => {
    // Start the WebP-conversion background loop. The endpoint converts every
    // non-webp image currently used in a product. No limit — the loop runs
    // until the queue is empty or the user pauses it.
    try {
      const { data } = await axios.post(`${API}/api/media/convert-webp`, {}, { headers: getHeaders() });
      toast.success(data.message);
      await fetchWebpStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Feil ved WebP-konvertering';
      if (err.response?.status === 409) toast.info(msg);
      else toast.error(msg);
    }
  };

  const pauseWebp = async () => {
    try {
      await axios.post(`${API}/api/media/webp/pause`, {}, { headers: getHeaders() });
      toast.success('WebP-konvertering pauset');
      await fetchWebpStatus();
    } catch {
      toast.error('Kunne ikke pause');
    }
  };

  const resetWebp = async () => {
    if (!window.confirm('Nullstill kun feilede WebP-konverteringer? Vellykkede konverteringer beholdes.')) return;
    try {
      const { data } = await axios.post(`${API}/api/media/webp/reset`, {}, { headers: getHeaders() });
      toast.success(data.message || 'Nullstilt');
      await fetchWebpStatus();
    } catch {
      toast.error('Kunne ikke nullstille');
    }
  };

  const runAltText = async () => {
    // Start the AI alt-text background loop (POST /api/media/alt-text/start).
    // The loop iterates over every image the tenant has where alt_text="" AND
    // alt_generated≠True. We don't enqueue it ourselves — the start endpoint
    // does the enqueueing AND spawns the async task in one call.
    try {
      const { data } = await axios.post(`${API}/api/media/alt-text/start`, {}, { headers: getHeaders() });
      toast.success(`AI Alt-tekst startet for ${data.total} bilder.`);
      await fetchAltStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Feil ved alt-tekst generering';
      if (msg.toLowerCase().includes('kjører') || msg.toLowerCase().includes('running')) toast.info(msg);
      else toast.error(msg);
    }
  };

  const pauseAltText = async () => {
    try {
      await axios.post(`${API}/api/media/alt-text/pause`, {}, { headers: getHeaders() });
      toast.success('AI Alt-tekst pauset');
      await fetchAltStatus();
    } catch {
      toast.error('Kunne ikke pause');
    }
  };

  const resetAltText = async () => {
    if (!window.confirm('Nullstill AI Alt-tekst fremgang? Bilder må prosesseres på nytt.')) return;
    try {
      const { data } = await axios.post(`${API}/api/media/alt-text/reset`, {}, { headers: getHeaders() });
      toast.success(data.message || 'Nullstilt');
      await fetchAltStatus();
    } catch {
      toast.error('Kunne ikke nullstille');
    }
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-8 w-48 bg-muted animate-pulse rounded" /><div className="h-64 bg-muted animate-pulse rounded-md" /></div>;
  }

  const scanProgress = (() => {
    if (scanStatus?.phase === 'mapping_products' && scanStatus?.mapped_total > 0) {
      return Math.round((scanStatus.mapped_progress / scanStatus.mapped_total) * 100);
    }
    if (scanStatus?.total_media > 0) {
      return Math.round((scanStatus.scanned / scanStatus.total_media) * 100);
    }
    return 0;
  })();

  return (
    <div className="space-y-6" data-testid="media-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-extrabold">Media</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Opprydding av mediebiblioteket</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshScan('verify')}
            data-testid="verify-unused-btn"
            className="gap-1"
            title="Sjekker kun bilder merket som ubrukt på nytt — bevarer alt annet. Bruk dette først hvis bilder ser feilmerket ut. ~30s"
          >
            <ArrowsClockwise size={12} /> Sjekk ubrukte (30s)
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshScan('incremental')}
            data-testid="incremental-scan-btn"
            className="gap-1"
            title="Henter kun nye bilder fra siste 30 dager."
          >
            <ArrowsClockwise size={12} /> Nye siste 30d
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshScan('remap')}
            data-testid="full-remap-btn"
            className="gap-1"
            title="Nullstiller ALLE koblinger og bygger fra nytt. Bruk kun hvis data ser korrupt ut. ~2-3 min"
          >
            <ArrowsClockwise size={12} /> Full re-mapping
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshScan('full')}
            data-testid="full-scan-btn"
            className="gap-1"
            title="Henter alle bilder fra WordPress + full re-mapping. Bruk kun ved første gangs skann."
          >
            <ArrowsClockwise size={12} /> Full skann
          </Button>
          <Button variant="outline" size="sm" onClick={exportUnused} data-testid="export-unused-btn" className="gap-1" title="Last ned alle ubrukte bilder som Excel-fil med detaljert info">
            <DownloadSimple size={12} /> Excel
          </Button>

          {selectedMedia.length > 0 && (
            <Button variant="destructive" size="sm" onClick={runBatchDelete} disabled={deleting} data-testid="delete-selected-btn" className="gap-1">
              <Trash size={12} /> {deleting ? 'Sletter...' : `Slett ${selectedMedia.length} markerte`}
            </Button>
          )}
        </div>
      </div>

      {/* Scan Status */}
      {scanStatus && scanStatus.phase !== 'idle' && (
        <Card className="border border-border" data-testid="scan-status-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-2">
              {scanStatus.running ? (
                <SpinnerGap size={16} className="text-primary animate-spin" />
              ) : scanStatus.phase === 'completed' ? (
                <CheckCircle size={16} className="text-success" />
              ) : (
                <Warning size={16} className="text-warning" />
              )}
              <span className="text-sm font-medium">
                {scanStatus.phase === 'fetching_media' && `Henter bilder fra WordPress... (${scanStatus.scanned}/${scanStatus.total_media})`}
                {scanStatus.phase === 'mapping_products' && `Mapper bilder til produkter... (${scanStatus.mapped_progress || 0}/${scanStatus.mapped_total || '?'})`}
                {scanStatus.phase === 'verifying_unused' && `Sjekker ubrukte bilder... (${scanStatus.mapped_progress || 0}/${scanStatus.mapped_total || '?'})`}
                {scanStatus.phase === 'remapping' && 'Forbereder re-mapping...'}
                {scanStatus.phase === 'scanning_variations' && 'Sjekker variant-bilder...'}
                {scanStatus.phase === 'wp_pages' && 'Skanner WordPress-sider...'}
                {scanStatus.phase === 'wp_parent_fallback' && 'Sikkerhetsnett: wp_parent-fallback...'}
                {scanStatus.phase === 'finding_duplicates' && 'Finner duplikater...'}
                {scanStatus.phase === 'completed' && 'Skanning fullført'}
                {scanStatus.phase === 'cancelled' && 'Skanning avbrutt'}
                {scanStatus.phase === 'failed' && `Feil: ${scanStatus.error}`}
              </span>
              {scanStatus.running && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto gap-1"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/media/scan/cancel`, {}, { headers: getHeaders() });
                      toast.success('Skanning avbrutt');
                      setTimeout(fetchScanStatus, 800);
                    } catch (err) {
                      toast.error('Kunne ikke avbryte');
                    }
                  }}
                  data-testid="cancel-scan-btn"
                >
                  Avbryt
                </Button>
              )}
            </div>
            {scanStatus.running && <Progress value={scanProgress} className="h-1.5" />}
          </CardContent>
        </Card>
      )}

      {/* AI Alt-tekst control (AutoGuard-style: start/pause/reset + progress) */}
      <Card className="border border-border" data-testid="alt-text-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  altStatus?.running ? 'bg-success animate-pulse' : 'bg-muted-foreground'
                }`}
              />
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-sm">AI Alt-tekst</h3>
                <p className="text-xs text-muted-foreground">
                  {altStatus?.running
                    ? `Behandler bilder... ${altStatus.processed || 0} / ${altStatus.total || 0}`
                    : altStatus?.paused
                      ? 'Pauset — trykk Start for å fortsette'
                      : overview?.no_alt_text > 0
                        ? `${overview.no_alt_text.toLocaleString('nb-NO')} bilder mangler alt-tekst`
                        : 'Alle bilder har alt-tekst 🎉'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {altStatus?.running ? (
                <Button variant="outline" size="sm" onClick={pauseAltText} data-testid="alt-pause-btn" className="gap-1">
                  Pause
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={runAltText}
                  data-testid="alt-start-btn"
                  disabled={overview?.no_alt_text === 0 && !altStatus?.paused}
                  className="gap-1"
                >
                  <Eye size={12} /> {altStatus?.paused ? 'Fortsett' : 'Start AI Alt-tekst'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={resetAltText}
                data-testid="alt-reset-btn"
                disabled={altStatus?.running}
                title="Nullstill fremgang og kjør på alle bilder på nytt"
                className="gap-1"
              >
                <ArrowsClockwise size={12} /> Nullstill
              </Button>
            </div>
          </div>

          {/* Stats grid — same shape as AutoGuard's progress card so the user
              gets familiar with the same layout across the app. */}
          {(altStatus?.running || altStatus?.processed > 0) && (
            <>
              <Progress
                value={
                  altStatus?.total
                    ? Math.round(((altStatus.processed || 0) / altStatus.total) * 100)
                    : 0
                }
                className="h-1.5 mt-3"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-center">
                <div>
                  <p className="font-heading text-lg font-bold tabular-nums">{altStatus.total || 0}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Totalt</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold tabular-nums text-success">{altStatus.processed || 0}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Behandlet</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold tabular-nums">
                    {Math.max((altStatus.total || 0) - (altStatus.processed || 0), 0)}
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Gjenstår</p>
                </div>
                <div>
                  <p className={`font-heading text-lg font-bold tabular-nums ${altStatus.errors > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {altStatus.errors || 0}
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Feil</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* WebP conversion control (same AutoGuard-style layout as Alt-text card) */}
      <Card className="border border-border" data-testid="webp-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                  webpStatus?.running ? 'bg-success animate-pulse' : 'bg-muted-foreground'
                }`}
              />
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-sm">WebP-konvertering</h3>
                <p className="text-xs text-muted-foreground">
                  {webpStatus?.running
                    ? `Konverterer bilder... ${webpStatus.processed || 0} / ${webpStatus.total || 0}`
                    : webpStatus?.paused
                      ? 'Pauset — trykk Start for å fortsette'
                      : overview?.quality?.not_webp > 0
                        ? `${overview.quality.not_webp.toLocaleString('nb-NO')} bilder kan konverteres til WebP (raskere sidelast)`
                        : 'Alle bilder er WebP 🎉'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {webpStatus?.running ? (
                <Button variant="outline" size="sm" onClick={pauseWebp} data-testid="webp-pause-btn" className="gap-1">
                  Pause
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  onClick={runWebpConvert}
                  data-testid="webp-start-btn"
                  disabled={overview?.quality?.not_webp === 0 && !webpStatus?.paused}
                  className="gap-1"
                >
                  <Image size={12} /> {webpStatus?.paused ? 'Fortsett' : 'Start WebP-konvertering'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={resetWebp}
                data-testid="webp-reset-btn"
                disabled={webpStatus?.running}
                title="Tøm tidligere feil-flagg slik at de prøves på nytt"
                className="gap-1"
              >
                <ArrowsClockwise size={12} /> Nullstill
              </Button>
            </div>
          </div>

          {(webpStatus?.running || webpStatus?.processed > 0) && (
            <>
              <Progress
                value={
                  webpStatus?.total
                    ? Math.round(((webpStatus.processed || 0) / webpStatus.total) * 100)
                    : 0
                }
                className="h-1.5 mt-3"
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-center">
                <div>
                  <p className="font-heading text-lg font-bold tabular-nums">{webpStatus.total || 0}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Totalt</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold tabular-nums text-success">{webpStatus.processed || 0}</p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Konvertert</p>
                </div>
                <div>
                  <p className="font-heading text-lg font-bold tabular-nums">
                    {Math.max((webpStatus.total || 0) - (webpStatus.processed || 0), 0)}
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Gjenstår</p>
                </div>
                <div>
                  <p className={`font-heading text-lg font-bold tabular-nums ${webpStatus.errors > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {webpStatus.errors || 0}
                  </p>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Feil</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>




      {/* Overview Cards */}
      {overview && overview.total > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="media-overview">
            <Card className="border border-border">
              <CardContent className="p-4 text-center">
                <Image size={20} className="mx-auto text-primary mb-1" />
                <p className="font-heading text-2xl font-bold">{overview.total.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Totalt bilder</p>
              </CardContent>
            </Card>
            <Card className="border border-border">
              <CardContent className="p-4 text-center">
                <CheckCircle size={20} className="mx-auto text-success mb-1" />
                <p className="font-heading text-2xl font-bold">{overview.used.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">I bruk</p>
              </CardContent>
            </Card>
            <Card className="border border-border bg-destructive/5">
              <CardContent className="p-4 text-center">
                <Trash size={20} className="mx-auto text-destructive mb-1" />
                <p className="font-heading text-2xl font-bold text-destructive">{overview.unused.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Ubrukte</p>
              </CardContent>
            </Card>
            <Card className="border border-border">
              <CardContent className="p-4 text-center">
                <Copy size={20} className="mx-auto text-warning mb-1" />
                <p className="font-heading text-2xl font-bold text-warning">{overview.duplicate_groups}</p>
                <p className="text-xs text-muted-foreground">Duplikat-grupper</p>
              </CardContent>
            </Card>
            <Card className="border border-border">
              <CardContent className="p-4 text-center">
                <Warning size={20} className="mx-auto text-muted-foreground mb-1" />
                <p className="font-heading text-2xl font-bold">{overview.no_alt_text}</p>
                <p className="text-xs text-muted-foreground">Mangler alt-tekst</p>
              </CardContent>
            </Card>
          </div>

          {/* Quality / Performance */}
          {overview.quality && (
            <Card className="border border-border" data-testid="quality-card">
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold text-sm mb-3">Ytelse & kvalitet</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center">
                    <p className="font-heading text-lg font-bold text-destructive">{overview.quality.very_large_2mb}</p>
                    <p className="text-xs text-muted-foreground">Over 2MB</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-lg font-bold text-warning">{overview.quality.oversized_500kb}</p>
                    <p className="text-xs text-muted-foreground">Over 500KB</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-lg font-bold text-warning">{overview.quality.not_webp}</p>
                    <p className="text-xs text-muted-foreground">Ikke WebP (i bruk)</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-lg font-bold">{overview.quality.total_storage_mb} MB</p>
                    <p className="text-xs text-muted-foreground">Total lagring</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-lg font-bold text-destructive">{overview.quality.unused_storage_mb} MB</p>
                    <p className="text-xs text-muted-foreground">Ubrukt lagring</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Tabs */}
      <Tabs defaultValue="unused" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="w-max" data-testid="media-tabs">
            <TabsTrigger value="unused">Ubrukte ({overview?.unused || 0})</TabsTrigger>
            <TabsTrigger value="duplicates">Duplikater ({overview?.duplicate_groups || 0})</TabsTrigger>
            <TabsTrigger value="quality">Kvalitet ({overview?.quality?.oversized_500kb || 0})</TabsTrigger>
          </TabsList>
        </div>

        {/* Unused Tab */}
        <TabsContent value="unused" className="space-y-4">
          {/* Beskyttelses-banner */}
          <Card className="border border-amber-300/40 bg-amber-50/40 dark:bg-amber-500/5" data-testid="protection-banner">
            <CardContent className="p-3 flex items-center gap-3 text-xs">
              <FilePdf size={20} weight="duotone" className="text-red-500 shrink-0" />
              <div>
                <span className="font-semibold">PDF-er og fonter slettes aldri av Sortilize.</span>
                {' '}Dette beskytter strikkeoppskrifter, mønstre og tema-ressurser. Slik fil vil få 403-feil hvis du prøver, og holdes utenfor batch-sletting automatisk.
              </div>
            </CardContent>
          </Card>

          {/* Trust badge: show what was scanned so user can trust the unused list.
              Closes the "er du sikker på at de er ubrukt?"-gap. */}
          {scanStatus?.layers_completed && scanStatus.layers_completed.length > 0 && (
            <Card className="border border-success/40 bg-success/5" data-testid="scan-evidence-card">
              <CardContent className="p-3">
                <div className="text-xs space-y-2">
                  <p className="font-semibold flex items-center gap-1.5">
                    <Image size={14} weight="bold" className="text-success" />
                    Sjekket grundig mot {scanStatus.layers_completed.length} kilder før noe ble merket som ubrukt:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {scanStatus.layers_completed.map((layer) => (
                      <span
                        key={layer}
                        className="inline-flex items-center gap-1 bg-background border border-border rounded-md px-1.5 py-0.5 text-[10px]"
                      >
                        <span className="text-success">✓</span> {layer}
                      </span>
                    ))}
                  </div>
                  <p className="text-muted-foreground italic">
                    Selv om vi sjekker alt dette så kan bilder fra ren CSS-bakgrunn i temaet eller eksterne CDN ikke oppdages. Bruk "Beskytt"-knappen på tvil.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {unused.total === 0 ? (
            <Card className="border border-border">
              <CardContent className="p-8 text-center text-muted-foreground">
                {scanStatus?.phase === 'completed' ? 'Ingen ubrukte bilder funnet!' : 'Vent til skanningen er ferdig...'}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Controls bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={selectAllOnPage} data-testid="select-all-media">
                    {selectedMedia.length === unused.items.length ? 'Fjern alle' : 'Marker alle på siden'}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedMedia.length > 0 ? `${selectedMedia.length} valgt` : `${unused.total} ubrukte bilder`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Vis:</span>
                  <Select value={String(perPage)} onValueChange={changePerPage}>
                    <SelectTrigger className="w-[80px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="75">75</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Image grid */}
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2">
                {unused.items.map((item) => {
                  const isSelected = selectedMedia.includes(item.id);
                  return (
                    <div 
                      key={item.id} 
                      className={`border rounded-md overflow-hidden cursor-pointer transition-all ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-muted-foreground/30'}`}
                      onClick={() => toggleSelectMedia(item.id)}
                      data-testid={`unused-${item.id}`}
                    >
                      <div className="relative aspect-square bg-muted">
                        <MediaThumb
                          item={item}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <CheckCircle size={12} className="text-white" weight="bold" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => protectMedia(item.id, e)}
                          className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[9px] bg-amber-500 hover:bg-amber-600 text-white rounded shadow"
                          title="Marker som beskyttet (i bruk i tema/karusell)"
                          data-testid={`protect-${item.id}`}
                        >
                          🛡 Beskytt
                        </button>
                      </div>
                      <div className="p-1.5">
                        <p className="text-[10px] truncate">{item.title || item.filename?.split('/').pop()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {unused.pages > 1 && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Side {unused.page} av {unused.pages}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={unused.page <= 1} onClick={() => fetchUnused(unused.page - 1)}>Forrige</Button>
                    <Button variant="outline" size="sm" disabled={unused.page >= unused.pages} onClick={() => fetchUnused(unused.page + 1)}>Neste</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Duplicates Tab */}
        <TabsContent value="duplicates" className="space-y-4">
          {duplicates.total === 0 ? (
            <Card className="border border-border">
              <CardContent className="p-8 text-center text-muted-foreground">
                {scanStatus?.phase === 'completed' ? 'Ingen duplikater funnet!' : 'Vent til skanningen er ferdig...'}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{duplicates.total} grupper med duplikat-bilder</p>
              {duplicates.groups.map((group, gi) => (
                <Card key={gi} className="border border-border" data-testid={`dup-group-${gi}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-sm flex items-center gap-2">
                      <Copy size={14} className="text-warning" />
                      {group.filename_base} — {group.count} kopier
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {group.items?.map((item) => (
                        <div key={item.id} className="border border-border rounded-md p-2">
                          <MediaThumb
                            item={item}
                            alt=""
                            className="w-full h-24 object-cover rounded mb-2"
                          />
                          <p className="text-xs font-mono truncate">ID: {item.id}</p>
                          <p className="text-xs text-muted-foreground">{item.date?.split('T')[0]}</p>
                          {item.used_in?.length > 0 ? (
                            <div className="mt-1">
                              <Badge className="bg-success/10 text-success border-success/20 text-xs">Brukt i {item.used_in.length} produkt(er)</Badge>
                              <div className="mt-1 space-y-0.5">
                                {item.used_in.slice(0, 2).map((u, ui) => (
                                  <p key={ui} className="text-xs text-muted-foreground truncate cursor-pointer hover:text-primary" onClick={() => navigate(`/products/${u.product_id}`)}>
                                    → {u.product_name}
                                  </p>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-xs mt-1">Ubrukt</Badge>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full mt-2 text-xs gap-1"
                            onClick={() => replaceDuplicate(item.id, group.items.filter(i => i.id !== item.id).map(i => i.id))}
                          >
                            <CheckCircle size={10} /> Behold denne
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Quality Tab */}
        <TabsContent value="quality" className="space-y-4">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Bilder som påvirker ytelsen</CardTitle>
              <p className="text-xs text-muted-foreground">Store bilder bremser nettsiden for kundene. Bilder over 500KB bør optimaliseres, og WebP-format er raskest.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border border-destructive/20 bg-destructive/5 rounded-md">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-destructive">Over 2MB</p>
                  <p className="font-heading text-2xl font-bold mt-1">{overview?.quality?.very_large_2mb || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Disse bremser siden merkbart</p>
                </div>
                <div className="p-4 border border-warning/20 bg-warning/5 rounded-md">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-warning">Over 500KB</p>
                  <p className="font-heading text-2xl font-bold mt-1">{overview?.quality?.oversized_500kb || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Bør optimaliseres</p>
                </div>
                <div className="p-4 border border-border rounded-md">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Ikke WebP (i bruk)</p>
                  <p className="font-heading text-2xl font-bold mt-1">{overview?.quality?.not_webp || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">PNG/JPG som bør konverteres</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Tips: WebP-format gir typisk 25-34% mindre filer enn JPG/PNG med samme kvalitet. Google anbefaler WebP for raskere lasting.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Scan history — last 10 scans, lets you see progress over time */}
      {history.length > 0 && (
        <Card className="border border-border" data-testid="scan-history-card">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <ClockCounterClockwise size={16} weight="bold" />
              Skann-historikk
              <Badge variant="secondary" className="ml-auto">{history.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scan-history-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-2 font-semibold">Tid</th>
                    <th className="text-left px-4 py-2 font-semibold">Type</th>
                    <th className="text-right px-4 py-2 font-semibold">Varighet</th>
                    <th className="text-right px-4 py-2 font-semibold">Reddet</th>
                    <th className="text-right px-4 py-2 font-semibold">Ubrukt etter</th>
                    <th className="text-right px-4 py-2 font-semibold">Duplikat-grupper</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => {
                    const typeLabel = {
                      verify_unused: 'Sjekk ubrukte',
                      full_remap: 'Full re-mapping',
                      full: 'Full skann',
                      last_30_days: 'Inkrementell',
                    }[h.scan_type] || h.scan_type;
                    const durSec = h.duration_ms ? Math.round(h.duration_ms / 1000) : null;
                    const durLabel = durSec
                      ? durSec >= 60
                        ? `${Math.floor(durSec / 60)}m ${durSec % 60}s`
                        : `${durSec}s`
                      : '—';
                    const recovered = (h.recovered_from_unused || 0) + (h.fallback_recovered || 0);
                    return (
                      <tr key={`${h.completed_at}-${i}`} className="border-b border-border/60 hover:bg-muted/20">
                        <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                          {h.completed_at ? new Date(h.completed_at).toLocaleString('nb-NO') : '—'}
                        </td>
                        <td className="px-4 py-2">{typeLabel}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs">{durLabel}</td>
                        <td className={`px-4 py-2 text-right font-mono text-xs ${recovered > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                          {recovered > 0 ? `+${recovered}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-xs">{h.unused_after ?? '—'}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs">{h.duplicates_found ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
